import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../../Components/Toast";
import LoadingInComponent from "../../Components/LoadingInComponent";
import FormCard from "../../Components/FormCard";
import TextboxSet from "../../Components/TextboxSet";
import DropdownSet from "../../Components/DropdownSet";
import Breadcrumb from "../../Components/Breadcrumb";
import { InfoCircle } from "iconsax-react";
import Header from "../../Components/Header";
import Button from "../../Components/Button";
import {
  UPDATE_USER_API,
  EVENT_OPTIONS_API,
  ROLE_OPTIONS_API,
  GET_USER_DETAILS_API,
} from "../../Constant/api";

const EditUser = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state;

  const [loading, setLoading] = useState(true);
  const [getUserInfo, setUserInfo] = useState(null);
  const [getEventOptions, setEventOptions] = useState([]);
  const [getRoleOptions, setRoleOptions] = useState([]);

  const fetchEventList = useCallback(() => {
    return axios.get(EVENT_OPTIONS_API, {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
      },
    });
  }, []);

  const fetchRoleList = useCallback(() => {
    return axios.get(ROLE_OPTIONS_API, {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
      },
    });
  }, []);

  const fetchUserDetails = useCallback(() => {
    return axios.get(GET_USER_DETAILS_API, {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
      },
      params: { id: userId },
    });
  }, [userId]);

  useEffect(() => {
    setLoading(true);

    Promise.all([fetchEventList(), fetchRoleList(), fetchUserDetails()])
      .then(([eventRes, roleRes, userRes]) => {
        setEventOptions(eventRes.data.data);
        setRoleOptions(roleRes.data.data);

        const data = userRes.data.data;
        setUserInfo({
          id: data.user_info.id,
          name: data.user_info.name,
          email: data.user_info.email,
          password: data.user_info.password,
          event_id: data.event_info?.id || "",
          role_id: data.role_info?.id || "",
        });

        setLoading(false);
      })
      .catch(() => {
        toast("Failed to fetch user information.", "error");
        setLoading(false);
      });
  }, [userId, fetchEventList, fetchRoleList, fetchUserDetails, toast]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropDownChange = (name, value) => {
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!emailRegex.test(getUserInfo.email)) {
      toast("Please enter a valid email address.", "error");
      return;
    }

    setLoading(true);
    axios
      .put(UPDATE_USER_API, getUserInfo, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then(() => {
        toast("User updated successfully.", "success");
        navigate("/admin/user-list");
      })
      .catch((err) => {
        toast(err.response?.data?.message || "Failed to update user.", "error");
        setLoading(false);
      });
  };

  const breadcrumbItems = [
    { label: "User List", to: "/admin/user-list" },
    { label: getUserInfo?.name || "User" },
    { label: "Edit" },
  ];

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto">
      <Header noSidebar={true} />
      <LoadingInComponent isLoading={loading || !getUserInfo}>
        {getUserInfo && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[24px]">
            <Breadcrumb items={breadcrumbItems} />

            <h1 className="text-[29px]">Edit User</h1>

            <div className="flex flex-col md:flex-row w-full gap-[24px]">
              <FormCard title="User Information">
                <div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-[24px] p-[24px]">
                  <TextboxSet
                    type="text"
                    name="name"
                    label="Name"
                    value={getUserInfo.name}
                    required
                    onChange={handleOnChange}
                  />
                  <TextboxSet
                    type="email"
                    name="email"
                    label="Email"
                    value={getUserInfo.email}
                    required
                    onChange={handleOnChange}
                  />
                  <div className="flex flex-col">
                    <TextboxSet
                      type="password"
                      name="password"
                      label="Password"
                      value={getUserInfo.password}
                      required
                      onChange={handleOnChange}
                    />
                    <p className="flex text-[#A1A7B2] text-[11px] gap-[4px] items-center mx-[12px]">
                      <InfoCircle size="11" variant="Bold" />
                      Leave this field empty if you don't want change password
                    </p>
                  </div>

                  <DropdownSet
                    name="event_id"
                    label="Event"
                    value={getUserInfo.event_id}
                    options={getEventOptions}
                    onChange={handleDropDownChange}
                  />

                  <div className="lg:col-span-2">
                    <DropdownSet
                      name="role_id"
                      label="Role"
                      value={getUserInfo.role_id}
                      options={getRoleOptions}
                      required
                      onChange={handleDropDownChange}
                    />
                  </div>
                </div>
              </FormCard>
            </div>

            <div className="flex gap-[8px]">
              <Button onClick={handleUpdate} loading={loading}>
                Save Changes
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/admin/user-list")}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default EditUser;
